import { dropStaleWorkers } from '@mailserver/web-shared';

/**
 * Service-worker lifecycle for the installable webmail. Registration only happens in a production
 * build: a service worker in front of the Vite dev server serves stale modules and makes hot reload
 * behave in ways that waste an afternoon.
 */
/**
 * Registered from the app's own base, so the worker's default scope is this application and nothing
 * else — `/` on the mail client's own host (`/mail/` only while the two apps shared one). It used to be
 * `/sw.js` on the shared host, which scoped a mail client's offline cache over the admin console.
 */
const SW_URL = `${import.meta.env.BASE_URL}sw.js`;

export function registerServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    // Ordered, not concurrent: dropping the pre-split root worker also empties every cache on this
    // origin, so registering first would hand the new worker's freshly-filled shell to that sweep.
    void dropStaleWorkers(import.meta.env.BASE_URL)
      .then(() => navigator.serviceWorker.register(SW_URL))
      .catch(() => {
        // an unregistered worker only costs the offline cache; the app is fully functional without it
      });
  });
}

/**
 * Drop everything the worker cached for this mailbox. Called on sign-out: the mail cache is keyed by
 * URL alone, so without this the next person to use the browser could read the previous session's
 * mail while offline.
 */
export async function clearOfflineMail(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  reg?.active?.postMessage({ type: 'clear-mail-cache' });
}
